import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const VIEW = 240;        // circular viewport size (px)
const OUTPUT = 512;      // exported image size (px)
const MAX_ZOOM = 4;

interface Offset {
  x: number;
  y: number;
}

interface Props {
  file: File | null;
  open: boolean;
  onApply: (file: File) => void;
  onCancel: () => void;
}

/**
 * Circular photo cropper — pan (drag) + zoom (slider/wheel), exports a
 * 512×512 PNG File with a circular alpha mask. No external deps.
 */
export function PhotoCropper({ file, open, onApply, onCancel }: Props) {
  const [src, setSrc] = useState<string>('');
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [natural, setNatural] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const dragRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);

  // Load picked file into a data URL
  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSrc(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsDataURL(file);
  }, [file]);

  // Fit image to cover the circle when it loads
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    setNatural({ w, h });
    const cover = VIEW / Math.min(w, h);
    setMinZoom(cover);
    setZoom(cover);
    setOffset({ x: (VIEW - w * cover) / 2, y: (VIEW - h * cover) / 2 });
  };

  /** Clamp the offset so the image always covers the full circle. */
  const clamp = useCallback((z: number, x: number, y: number): Offset => {
    const scaledW = natural.w * z;
    const scaledH = natural.h * z;
    const minX = Math.min(0, VIEW - scaledW);
    const minY = Math.min(0, VIEW - scaledH);
    const maxX = Math.max(0, VIEW - scaledW);
    const maxY = Math.max(0, VIEW - scaledH);
    return {
      x: Math.max(minX, Math.min(maxX, x)),
      y: Math.max(minY, Math.min(maxY, y)),
    };
  }, [natural]);

  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(clamp(zoom, dragRef.current.ox + dx, dragRef.current.oy + dy));
  };
  const onMouseUp = () => {
    dragRef.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.max(minZoom, Math.min(MAX_ZOOM, zoom * (1 - e.deltaY * 0.001)));
    zoomAround(next, VIEW / 2, VIEW / 2);
  };

  /** Zoom keeping a specific viewport point fixed. */
  const zoomAround = (nextZoom: number, cx: number, cy: number) => {
    const ratio = nextZoom / zoom;
    const nx = cx - (cx - offset.x) * ratio;
    const ny = cy - (cy - offset.y) * ratio;
    setZoom(nextZoom);
    setOffset(clamp(nextZoom, nx, ny));
  };

  const handleApply = async () => {
    if (!src || !natural.w) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('image load failed'));
      img.src = src;
    });

    // Circular clip
    ctx.beginPath();
    ctx.arc(OUTPUT / 2, OUTPUT / 2, OUTPUT / 2, 0, Math.PI * 2);
    ctx.clip();

    // Mirror the on-screen transform, scaled up from VIEW to OUTPUT.
    const outScale = OUTPUT / VIEW;
    ctx.setTransform(outScale * zoom, 0, 0, outScale * zoom, offset.x * outScale, offset.y * outScale);
    ctx.drawImage(img, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
    if (!blob) return;
    const cropped = new File([blob], (file?.name?.replace(/\.[^.]+$/, '') || 'profile') + '.png', { type: 'image/png' });
    onApply(cropped);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Crop your photo</DialogTitle>
          <DialogDescription>Drag to reposition, scroll or use the slider to zoom.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          <div
            className="relative bg-slate-100 rounded-full overflow-hidden select-none"
            style={{ width: VIEW, height: VIEW, cursor: dragRef.current ? 'grabbing' : 'grab' }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
          >
            {src && (
              <img
                src={src}
                onLoad={onImageLoad}
                draggable={false}
                alt=""
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: natural.w,
                  height: natural.h,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                  transformOrigin: '0 0',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>

          <div className="w-full space-y-1">
            <Label htmlFor="crop-zoom" className="text-xs text-slate-600">Zoom</Label>
            <input
              id="crop-zoom"
              type="range"
              min={minZoom}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(e) => zoomAround(parseFloat(e.target.value), VIEW / 2, VIEW / 2)}
              className="w-full"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleApply} disabled={!natural.w}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
